import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import { useInView } from 'react-intersection-observer';

import { TagsDisplay } from 'shared/base/components/display/tagsDisplay';

import { ReportCardWrapper } from '../cardWrapper';
import { ReportCardTableLoader } from './loader';
import { mapTableData } from '../../mappers';
import { ReportCardVariantProps } from '../../types';
import { useMaximisableQuery } from './hooks/maximisableQuery';

import { ReportCardTable } from './table';
import { extractColumns, statusFormatter } from './utils';

export const TableReportCard = ({
  cellFormatter,
  clearFilters,
  columnFormatter,
  description,
  query,
  sx,
  title,
}: ReportCardVariantProps) => {
  const {
    handleFieldSort,
    maximized,
    perPage,
    queryResult,
    queryResultMaximized,
    searchTerm,
    setMaximized,
    setSearchTerm,
    setTablePage,
    sortBy,
    sortDir,
    tablePage,
    triggerMaximizedFetch,
  } = useMaximisableQuery(query);
  const { inView, ref } = useInView({
    rootMargin: '200px',
    threshold: 0,
    triggerOnce: true,
  });
  const showSearch = query.baseQuery.includes('search_string');
  const hasResults = (queryResult?.data?.data.metadata.total_count || 0) > 0;

  useEffect(() => {
    if (inView) {
      triggerMaximizedFetch();
    }
  }, [inView, triggerMaximizedFetch]);

  const columns = query.params?.select ?? extractColumns(query.baseQuery) ?? [];
  const tagsIndex = columns.findIndex(
    (col: string) => col === 'tags' || col.toLowerCase().includes('as tags'),
  );

  const statusIndex = columns.findIndex(
    (col: string) => col === 'status' || col.toLowerCase().includes('as status'),
  );

  const handleCellFormatting = (cell: any, colIdx: number) => {
    if (tagsIndex !== -1 && colIdx === tagsIndex) {
      return <TagsDisplay tags={cell} />;
    }

    if (statusIndex !== -1 && colIdx === statusIndex) {
      return statusFormatter(cell);
    }

    if (typeof cell === 'object') {
      return JSON.stringify(cell);
    }

    if (cellFormatter) {
      return cellFormatter(cell, colIdx);
    }

    return cell;
  };

  return (
    <Box ref={ref}>
      <ReportCardWrapper
        clearFilters={clearFilters}
        contentSx={{ alignItems: 'top', padding: 0 }}
        description={description}
        onMaximize={() => setMaximized(true)}
        onMinimize={() => setMaximized(false)}
        query={query.baseQuery}
        queryResult={maximized ? queryResultMaximized : queryResult}
        renderContent={(successfulResult) => {
          const mappedData = mapTableData(
            successfulResult.data.data,
            columnFormatter,
            handleCellFormatting,
          );
          const count = successfulResult.data.data.metadata.total_count ?? 0;

          return (
            <Stack height={'100%'} justifyContent={'space-between'} width={'100%'}>
              <ReportCardTable
                data={mappedData}
                handleFieldSort={handleFieldSort}
                sortBy={sortBy}
                sortDir={sortDir}
              />
              {count > perPage && (
                <TablePagination
                  component="div"
                  count={count}
                  onPageChange={(_, page) => setTablePage(page)}
                  page={tablePage}
                  rowsPerPage={successfulResult.data.data.metadata.page_size ?? 0}
                  rowsPerPageOptions={[]}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      backgroundColor: 'background.paper',
                      minHeight: '42px',
                    },
                    '& p': {
                      margin: '0 !important',
                    },
                    position: 'static',
                  }}
                />
              )}
            </Stack>
          );
        }}
        renderLoading={() => <ReportCardTableLoader columns={query.params.select || []} />}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showMaximize={hasResults}
        showSearch={showSearch}
        sx={sx}
        title={title}
      />
    </Box>
  );
};
